import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Button, Typography, Tag } from '@navi/web-ui/lib/primitives';
import Download from '@cp/src/assets/images/icons/Download';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@cp/components/DateTimePicker/constants';
import styles from './AddressIngestion.module.scss';
import { CheckIcon, CancelIcon } from '@navi/web-ui/lib/icons';
import CircularLoader from '../Enach/Constants/CircularLoaderIconCopy';
import { ADDRESS_INGESTION_FILE_STATUS } from './constants';
import { getOriginalFile, fetchErrorReport } from './actions/AddressIngestionActions';

export const useColumnDefs = (): {
  columnDefs: ColDef[];
} => {
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        wrapHeaderText: true,
        width: 220,
        wrapText: true,
        headerComponentParams: { width: 220 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <div className={styles.fileNameContainer}>
              <Typography variant="p4" color="var(--grayscale-2)">
                {data?.originalFileName || '-'}
              </Typography>

              <Button
                variant="text"
                size="small"
                onClick={() => getOriginalFile(data?.originalFileReferenceId)}
              >
                <Download />
              </Button>
            </div>
          );
        }
      },
      {
        field: 'totalEntries',
        headerName: 'Total Addresses',
        wrapHeaderText: true,
        width: 120,
        wrapText: true,
        headerComponentParams: { width: 120 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <Typography variant="p4" color="var(--grayscale-2)">
              {data?.totalEntries || '0'}
            </Typography>
          );
        }
      },
      {
        field: 'uploadedOn',
        headerName: 'Upload Date & Time',
        wrapHeaderText: true,
        width: 200,
        wrapText: true,
        headerComponentParams: { width: 200 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <Typography variant="p4" color="var(--grayscale-3)">
              {data?.createdAt ? dayjs(data.createdAt).format(DateTimeFormat.DATE_TIME) : '-'}
            </Typography>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        wrapHeaderText: true,
        width: 140,
        wrapText: true,
        headerComponentParams: { width: 140 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.status === ADDRESS_INGESTION_FILE_STATUS.DONE) {
            return (
              <div className={styles.tagWrapper}>
                <Tag
                  color="green"
                  variant="transparent"
                  label="Done"
                  startAdornment={<CheckIcon color="var(--green-base)" />}
                />
              </div>
            );
          } else if (data?.status === ADDRESS_INGESTION_FILE_STATUS.INPROGRESS) {
            return (
              <div className={styles.tagWrapper}>
                <Tag
                  color="yellow"
                  variant="transparent"
                  label="In Progress"
                  startAdornment={<CircularLoader fill="var(--pending-fill-color)" />}
                />
              </div>
            );
          } else {
            return (
              <div className={styles.tagWrapper}>
                <Tag
                  color="red"
                  variant="transparent"
                  label="Failed"
                  startAdornment={<CancelIcon color="var(--red-base)" />}
                />
              </div>
            );
          }
        }
      },
      {
        field: 'report',
        headerName: 'Failure Report',
        wrapHeaderText: true,
        width: 160,
        wrapText: true,
        headerComponentParams: { width: 160 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (data?.errorReportReferenceId) {
            return (
              <Button
                variant="text"
                size="small"
                onClick={() => fetchErrorReport(data?.errorReportReferenceId)}
              >
                <Download />
              </Button>
            );
          }
          return (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      }
    ],
    []
  );

  return { columnDefs };
};

export default useColumnDefs;
